import { QueryClient } from '@tanstack/react-query';
import {
  createLegacyAuthGatewayClient,
  createPentaApiClient,
} from '@penta/penta-api';
import { getStoredToken } from '../contexts/AuthContext';
import { Middleware } from 'openapi-fetch';

/**
 * Identifikace kanálu pro backend. Specifikace (v5.0.1) hlavičku deklaruje na
 * 147 operacích — stejně jako `Accept-Language` — a hodnota odpovídá enumu
 * `CommunicationChannel`.
 *
 * Posílá se na VŠECHNA volání včetně `POST /v1/token` — je to údaj o kanálu,
 * ne přihlašovací údaj, a backend ho potřebuje znát i u přihlášení.
 */
const COMMUNICATION_CHANNEL = 'WEB';

/**
 * Cesta `/v1/token` na kterékoliv base URL (`…/api/v1/token`).
 */
const isTokenEndpoint = (request: Request): boolean => {
  try {
    return new URL(request.url).pathname.endsWith('/v1/token');
  } catch {
    return false;
  }
};

/**
 * Jen `POST /v1/token` je záměrně nezautentizované: s prázdným kódem je to
 * challenge (server odpoví 401 s adresou přihlášení) a výměna kódu žádnou
 * autorizaci nemá.
 *
 * `DELETE /v1/token` je naopak REVOKACE tokenu. Dřív se tahle výjimka
 * rozhodovala podle podřetězce v celé URL, tedy bez ohledu na metodu — takže
 * odhlášení odcházelo bez `Authorization`, server neměl co zneplatnit a token
 * zůstával platný. V kombinaci s tím, že tokeny nemají expiraci, platily
 * donekonečna.
 */
const isLoginChallenge = (request: Request): boolean =>
  request.method === 'POST' && isTokenEndpoint(request);

export const createAuthMiddleware = (
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
): Middleware => ({
  onRequest: ({ request }) => {
    // Clone the request to safely mutate headers
    const newRequest = request.clone();
    const lang = getLanguage?.() || 'en';
    if (lang) {
      newRequest.headers.set('Accept-Language', lang);
    }
    newRequest.headers.set('X-Communication-Channel', COMMUNICATION_CHANNEL);

    // Pozor na metodu: bez tokenu smí odejít JEN POST /v1/token.
    if (isLoginChallenge(newRequest)) {
      return newRequest;
    }

    const token = getStoredToken();
    if (token) {
      newRequest.headers.set('Authorization', `Bearer ${token}`);
    }
    return newRequest;
  },
  onResponse: async ({ response, request }) => {
    // 401 jinde znamená neplatný token -> odhlásit.
    //
    // `/v1/token` je z toho vyňatý pro OBĚ metody, ale z jiných důvodů:
    // u POSTu je 401 očekávaná odpověď challenge, u DELETE už odhlášení běží a
    // spuštění `onUnauthorized` by nám pod rukama přesměrovalo na /login místo
    // /logout. Selhání revokace se proto hlásí přímo v odhlašovacím handleru,
    // ne tady — tiše se ztratit nesmí.
    if (
      response.status === 401 &&
      onUnauthorized &&
      !isTokenEndpoint(request)
    ) {
      console.warn(
        '[Auth] Token validation failed (401 Unauthorized) - logging out',
      );
      onUnauthorized();
    }
    return response;
  },
});

/**
 * App-level reaction to a 401: discard the token and restart the login flow.
 * Concurrent requests (e.g. /client and /user on dashboard mount) can each hit
 * 401 with a token invalidated outside the app, so the logout/redirect runs
 * only once per handler instance.
 */
export const createUnauthorizedHandler = (logout: () => void) => {
  let handled = false;
  return () => {
    if (handled) {
      return;
    }
    handled = true;
    logout();
    // replace() keeps the dead page out of history; /login without a token
    // continues with the standard redirect to the legacy IB login
    window.location.replace('/login');
  };
};

const getEnvVar = (
  key: 'VITE_API_URL' | 'VITE_LEGACY_AUTH_API_URL',
  placeholder: string,
) => {
  const val = window.__ENV__?.[key];
  if (val && val !== placeholder) {
    return val;
  }
  return import.meta.env[key];
};

export const setupLegacyAuthClient = (
  queryClient: QueryClient,
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
) => {
  return createLegacyAuthGatewayClient({
    queryClient,
    baseUrl:
      getEnvVar(
        'VITE_LEGACY_AUTH_API_URL',
        '__LEGACY_AUTH_API_URL_PLACEHOLDER__',
      ) || 'https://ma-dev.privatbanka.sk/api', // Runtime env, Build env, or default
    middlewares: [createAuthMiddleware(onUnauthorized, getLanguage)],
  });
};

export const setupPentaApiClient = (
  queryClient: QueryClient,
  onUnauthorized?: () => void,
  getLanguage?: () => string | null,
) => {
  return createPentaApiClient({
    queryClient,
    baseUrl:
      getEnvVar('VITE_API_URL', '__API_URL_PLACEHOLDER__') ||
      'https://ma-dev.privatbanka.sk/api',
    middlewares: [createAuthMiddleware(onUnauthorized, getLanguage)],
  });
};
